import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Trash2, Edit, Save, X, RefreshCw, PlusCircle } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import { PaymentCategory, loadPaymentCategories } from '@/lib/settingsConfig';

const DEFAULT_TENANT_ID = '1225f4cb-43ae-463d-a6b0-53f721362dbf';

const makeKey = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

interface FormState {
  id?: string;
  category_key: string;
  label: string;
  sort_order: string;
  is_active: boolean;
}

const emptyForm = (): FormState => ({
  category_key: '',
  label: '',
  sort_order: '100',
  is_active: true,
});

const PaymentCategoriesManager: React.FC = () => {
  const [categories, setCategories] = useState<PaymentCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm());

  const load = async () => {
    setLoading(true);
    const data = await loadPaymentCategories(DEFAULT_TENANT_ID);
    setCategories(data);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const startAdd = () => {
    setForm(emptyForm());
    setShowForm(true);
  };

  const startEdit = (cat: PaymentCategory) => {
    setForm({
      id: cat.id,
      category_key: cat.category_key,
      label: cat.label,
      sort_order: String(cat.sort_order ?? 100),
      is_active: cat.is_active,
    });
    setShowForm(true);
  };

  const update = <K extends keyof FormState>(field: K, value: FormState[K]) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'label' && !prev.id) next.category_key = makeKey(String(value));
      return next;
    });
  };

  const save = async () => {
    if (!form.label.trim()) {
      toast({ title: 'Name required', description: 'Please enter a reason name.', variant: 'destructive' });
      return;
    }
    const key = makeKey(form.category_key || form.label);
    if (!key) {
      toast({ title: 'Key required', description: 'A simple internal key is needed.', variant: 'destructive' });
      return;
    }

    setSaving(true);
    const payload = {
      tenant_id: DEFAULT_TENANT_ID,
      category_key: key,
      label: form.label.trim(),
      sort_order: Number(form.sort_order) || 100,
      is_active: form.is_active,
    };

    const query = form.id
      ? supabase.from('payment_categories').update(payload).eq('id', form.id).select()
      : supabase.from('payment_categories').insert([payload]).select();

    const { error } = await query;
    setSaving(false);

    if (error) {
      toast({ title: 'Not saved', description: error.message, variant: 'destructive' });
      return;
    }

    toast({ title: 'Saved', description: `Payment reason "${payload.label}" saved.` });
    setShowForm(false);
    setForm(emptyForm());
    await load();
  };

  const toggleActive = async (cat: PaymentCategory) => {
    const { error } = await supabase
      .from('payment_categories')
      .update({ is_active: !cat.is_active })
      .eq('id', cat.id);
    if (error) {
      toast({ title: 'Not updated', description: error.message, variant: 'destructive' });
      return;
    }
    await load();
  };

  const remove = async (cat: PaymentCategory) => {
    if (!window.confirm(`Delete payment reason "${cat.label}"?\n\nExisting driver payments already using it keep their value, but it will no longer be selectable.`)) {
      return;
    }
    const { error } = await supabase.from('payment_categories').delete().eq('id', cat.id);
    if (error) {
      toast({ title: 'Not deleted', description: error.message, variant: 'destructive' });
      return;
    }
    await load();
  };

  return (
    <Card className="shadow-lg bg-card border border-border">
      <CardHeader className="border-b border-border">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle className="text-xl text-primary flex items-center gap-2">
              <PlusCircle className="w-5 h-5" />
              Payment Reasons
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Reasons for an extra payment to a driver (sim card reimbursement, correcting an overpaid
              deduction, a bonus, etc.) — separate from deduction categories since these add to a driver's
              pay instead of coming off it. Appear in the driver Deductions form when "Credit" is selected.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={load} disabled={loading}>
              <RefreshCw className="w-4 h-4 mr-2" />
              {loading ? 'Refreshing...' : 'Refresh'}
            </Button>
            <Button onClick={startAdd} className="golden-button">
              <Plus className="w-4 h-4 mr-2" />
              Add Reason
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4 p-5">
        {showForm && (
          <div className="rounded-xl border border-yellow-900/30 bg-black/20 p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-foreground">
                {form.id ? 'Edit Payment Reason' : 'Add Payment Reason'}
              </h3>
              <Button type="button" variant="ghost" size="sm" onClick={() => setShowForm(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-yellow-500">Name *</Label>
                <Input value={form.label} onChange={(e) => update('label', e.target.value)} placeholder="e.g. Sim Card Reimbursement" />
              </div>
              <div>
                <Label className="text-yellow-500">Internal Key</Label>
                <Input value={form.category_key} onChange={(e) => update('category_key', e.target.value)} placeholder="e.g. sim_card" />
                <p className="mt-1 text-xs text-muted-foreground">Lowercase with underscores.</p>
              </div>
              <div>
                <Label className="text-yellow-500">Sort Order</Label>
                <Input type="number" value={form.sort_order} onChange={(e) => update('sort_order', e.target.value)} />
              </div>
            </div>

            <label className="flex items-center gap-2 rounded-lg border border-border bg-background/40 p-3 text-sm w-fit">
              <Checkbox checked={form.is_active} onCheckedChange={(c) => update('is_active', Boolean(c))} />
              <span>Active</span>
            </label>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button onClick={save} disabled={saving} className="golden-button">
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Reason'}
              </Button>
            </div>
          </div>
        )}

        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/40">
              <tr className="border-b border-border">
                <th className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground">Reason</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground">Key</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground">Status</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={4} className="px-3 py-8 text-center text-muted-foreground">Loading...</td></tr>
              ) : categories.length === 0 ? (
                <tr><td colSpan={4} className="px-3 py-8 text-center text-muted-foreground">No payment reasons yet. Add one to get started.</td></tr>
              ) : (
                categories.map((cat) => (
                  <tr key={cat.id} className={`border-b border-border last:border-b-0 ${!cat.is_active ? 'opacity-60' : ''}`}>
                    <td className="px-3 py-3 font-semibold text-foreground">{cat.label}</td>
                    <td className="px-3 py-3 text-xs text-muted-foreground">{cat.category_key}</td>
                    <td className="px-3 py-3">
                      <button
                        type="button"
                        onClick={() => toggleActive(cat)}
                        className={`rounded-full border px-2 py-1 text-xs font-medium ${
                          cat.is_active
                            ? 'border-green-700/50 bg-green-900/30 text-green-300'
                            : 'border-gray-700 bg-gray-900/40 text-gray-400'
                        }`}
                      >
                        {cat.is_active ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-3 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="outline" onClick={() => startEdit(cat)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => remove(cat)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

export default PaymentCategoriesManager;
